/**
 * manager-pdp.js
 * Gerencia a Product Detail Page (PDP).
 * Disponível como window._pdpManager (instância atual).
 *
 * Dependências (devem carregar antes):
 *   - window.CartManager    (manager-cart.js)
 *   - window.ButtonFeedback (manager-global-button-feedback.js)
 *
 * ─── Módulos ──────────────────────────────────────────────────────────────
 *
 *   AddToCartController     → intercepta form submit e delega ao CartManager
 *
 * ─── Inicialização via Liquid ─────────────────────────────────────────────
 *
 *   Na section pdp-main.liquid, dentro do bloco {% javascript %}:
 *
 *     if (typeof initPdpManager === 'function') initPdpManager('{{ section.id }}');
 *
 *   A seção é selecionada por .pdpMain--{sectionId}.
 *
 * ─── HTML esperado ────────────────────────────────────────────────────────
 *
 *   <section class="pdpMain--{sectionId}" [data-pdp-gallery]>
 *
 * *     <div id="product-image-1">...</div>
 *     <div id="product-image-2">...</div>
 *
 *     <!-- Âncoras do menu lateral -->
 *     <a href="#product-image-1" class="pdpMain__bannerMenu">...</a>
 *
 *     <!-- Um ou mais wrappers de produto (ex: produto + produto relacionado) -->
 *     <div data-product-wrapper>
 *
 *       <!-- Seletor de tamanho -->
 *       <div data-size-selector>
 *         <button data-size-toggle aria-expanded="false">Selecionar tamanho</button>
 *         <div data-size-popup hidden>
 *           <button data-size-option data-variant-id="111" data-size-label="P">P</button>
 *           <button data-size-option data-variant-id="222" data-size-label="M">M</button>
 *         </div>
 *         <span data-size-label></span>
 *       </div>
 *
 *       <!-- Formulário de produto -->
 *       <form action="/cart/add">
 *         <input type="hidden" name="id" data-size-input>
 *         <button type="button" data-add-to-cart disabled>Selecione o tamanho</button>
 *       </form>
 *
 *     </div>
 *
 *   </section>
 */

/* ─── AddToCartController ─────────────────────────────────────────────────── */

class AddToCartController {
  constructor(wrapper) {
    this._form   = wrapper.querySelector('form[action*="/cart/add"]') || wrapper.querySelector('form');
    this._button = wrapper.querySelector('[data-add-to-cart]');
    // Suporta data-size-input (size selector) ou name="id" (select/hidden input padrão)
    this._input  = wrapper.querySelector('[data-size-input]')
                    || wrapper.querySelector('[name="id"]');

    if (!this._form) return;

    this._onSubmit = this._onSubmit.bind(this);
    this._form.addEventListener('submit', this._onSubmit);
  }

  async _onSubmit(e) {
    e.preventDefault();

    if (this._button?.dataset.pdpNotify === 'true') {
      const selectedSize = this._form?.querySelector(
        'input[data-pdp-option-name="tamanho"]:checked, input[data-pdp-option-name="size"]:checked'
      )?.value || '';
      document.getElementById('pdp-unavailable-modal')?.classList.add('is-active');
      document.dispatchEvent(new CustomEvent('pdp:notify-open', { detail: { size: selectedSize } }));
      return;
    }

    const variantId = this._input?.value;
    const selectedVariant = this._input?.selectedOptions?.[0];
    const variantPrice = selectedVariant?.dataset.price;
    if ((variantId && this._button?.disabled) || (variantPrice !== undefined && Number(variantPrice) <= 0)) return;
    if (!variantId) {
      if (this._button) {
        const button = this._button;
        const anchor = button.closest('.pdp-bar__actions, .pdp-mobile-form') || button.parentElement;
        let message = anchor?.querySelector('[data-pdp-size-warning]');

        if (anchor && !message) {
          message = document.createElement('span');
          message.dataset.pdpSizeWarning = '';
          message.textContent = 'Selecione um tamanho';
          anchor.appendChild(message);
        }

        if (message) {
          message.style.setProperty('--pdp-warning-left', `${button.offsetLeft + (button.offsetWidth / 2)}px`);
          message.style.setProperty(
            '--pdp-warning-top',
            `${button.offsetTop - message.offsetHeight - 7}px`
          );
          window.clearTimeout(button._emptyVariantMessageTimer);
          message.classList.remove('is-visible');
          requestAnimationFrame(() => message.classList.add('is-visible'));
          button._emptyVariantMessageTimer = window.setTimeout(() => {
            message.classList.remove('is-visible');
          }, 2200);
        }
      }
      return;
    }

    const action  = this._button?.dataset.addAction || 'open_drawer';
    const cartUrl = this._button?.dataset.cartUrl   || '/cart';

    // Congela dimensões para evitar layout shift ao injetar SVG do ButtonFeedback
    const btn = this._button;
    if (btn) {
      btn.style.width  = `${btn.offsetWidth}px`;
      btn.style.height = `${btn.offsetHeight}px`;
    }

    const result = await window.CartManager?.add(variantId, {
      button: btn,
      action,
      variantPrice,
    });

    if (btn) {
      btn.style.width  = '';
      btn.style.height = '';
    }

    if (result?.success && action === 'open_cart') {
      window.location.href = cartUrl;
    }
  }

  destroy() {
    this._form?.removeEventListener('submit', this._onSubmit);
  }
}

/* ─── PdpManager ──────────────────────────────────────────────────────────── */

class VariantImageFilterController {
  constructor(section) {
    this._section = section;
    this._filterEnabled = section.dataset.pdpFilterVariantImages === 'true';
    this._hasColorLabeledImages = section.dataset.pdpHasColorLabeledImages === 'true';
    this._mobileSlider = section.querySelector('[data-splide-component="pdp-mobile-images"]');
    this._mobileList = this._mobileSlider?.querySelector('.splide__list');
    this._mobileSlides = this._mobileList ? Array.from(this._mobileList.children) : [];
    this._onChange = this._onChange.bind(this);

    section.addEventListener('change', this._onChange);
    this._apply(section.dataset.pdpSelectedColor);
  }

  _normalizeColor(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  _onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const optionName = input.dataset.pdpOptionName || '';
    if (!/^(cor|color)(-|$)/.test(optionName)) return;

    this._apply(input.value);
  }

  _apply(value) {
    const selectedColor = this._normalizeColor(value);
    const imageElements = Array.from(this._section.querySelectorAll('[data-pdp-image-color]'));
    const hasMatchingImage = imageElements.some((imageElement) => imageElement.dataset.pdpImageColor === selectedColor);
    const shouldFilter = this._filterEnabled
      && this._hasColorLabeledImages
      && selectedColor !== ''
      && hasMatchingImage;
    this._section.dataset.pdpSelectedColor = selectedColor;

    imageElements.forEach((imageElement) => {
      imageElement.hidden = shouldFilter && imageElement.dataset.pdpImageColor !== selectedColor;
    });

    const zoomRoot = this._section.querySelector('[data-pdp-zoom]');
    const zoomInstance = window.PdpZoomManager?.instances.get(zoomRoot);
    if (zoomInstance && !zoomRoot.hidden && zoomInstance.thumbs[zoomInstance.index]?.hidden) {
      const firstVisibleIndex = zoomInstance.thumbs.findIndex((thumb) => !thumb.hidden);
      if (firstVisibleIndex !== -1) zoomInstance.show(firstVisibleIndex, 0, false);
    }

    if (!this._mobileList) return;

    const visibleSlides = shouldFilter
      ? this._mobileSlides.filter((slide) => slide.dataset.pdpImageColor === selectedColor)
      : this._mobileSlides;

    if (this._mobileSlider.splide) {
      this._mobileSlider.splide.destroy();
      delete this._mobileSlider.splide;
    }

    this._mobileSlides.forEach((slide) => {
      if (visibleSlides.includes(slide)) {
        slide.hidden = false;
        this._mobileList.appendChild(slide);
      } else {
        slide.hidden = true;
        slide.remove();
      }
    });

    this._mobileSlider.dataset.splideArrows = visibleSlides.length > 1 ? 'true' : 'false';
    if (window.SplideManager) window.SplideManager.init(this._mobileSlider);
  }

  destroy() {
    this._section.removeEventListener('change', this._onChange);
  }
}

class VariantSelectionController {
  constructor(section) {
    this._section = section;
    this._onChange = this._onChange.bind(this);
    this._section.addEventListener('change', this._onChange);
    this._section.querySelectorAll('form[action*="/cart/add"]').forEach((form) => this._syncForm(form));
  }

  _onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.name?.startsWith('options[')) return;

    const form = input.closest('form[action*="/cart/add"]');
    if (!form || !this._section.contains(form)) return;

    this._syncForm(form);
  }

  _readSelection(form) {
    const radios = Array.from(form.querySelectorAll('input[type="radio"][name^="options["]'));
    const groupNames = [];
    radios.forEach((radio) => {
      if (!groupNames.includes(radio.name)) groupNames.push(radio.name);
    });

    return groupNames.map((name) => radios.find((radio) => radio.name === name && radio.checked)?.value || '');
  }

  _findVariantOption(select, selectedValues) {
    if (!select || !selectedValues.length || selectedValues.some((value) => !value)) return null;

    return Array.from(select.options).find((option) => {
      if (!option.value || !option.dataset.variantOptions) return false;
      try {
        const optionValues = JSON.parse(option.dataset.variantOptions);
        return optionValues.length === selectedValues.length
          && optionValues.every((value, index) => String(value) === String(selectedValues[index]));
      } catch (error) {
        return false;
      }
    }) || null;
  }

  _setButtonState(form, variantOption) {
    const button = form.querySelector('[data-add-to-cart]');
    if (!button) return;

    const addLabel = button.dataset.addLabel || 'Adicionar à sacola';
    const notifyLabel = 'Avise-me';
    const zeroPriceMessage = form.querySelector('[data-pdp-zero-price-message]');

    if (!variantOption) {
      button.disabled = false;
      button.setAttribute('aria-disabled', 'false');
      button.textContent = addLabel;
      button.dataset.pdpVariantReady = 'false';
      button.dataset.pdpNotify = 'false';
      button.dataset.pdpPriceBlocked = 'false';
      button.dataset.variantPrice = '';
      if (zeroPriceMessage) zeroPriceMessage.hidden = true;
      return;
    }

    const price = Number(variantOption.dataset.price || 0);
    const available = variantOption.dataset.available === 'true';
    const zeroPrice = price <= 0;
    const notify = !available && !zeroPrice;
    const canAdd = available && !zeroPrice;

    button.disabled = zeroPrice;
    button.setAttribute('aria-disabled', String(zeroPrice));
    button.textContent = notify ? notifyLabel : addLabel;
    button.dataset.pdpVariantReady = canAdd ? 'true' : 'false';
    button.dataset.pdpNotify = notify ? 'true' : 'false';
    button.dataset.pdpPriceBlocked = zeroPrice ? 'true' : 'false';
    button.dataset.variantPrice = String(price);

    if (zeroPriceMessage) zeroPriceMessage.hidden = !zeroPrice;
  }

  _syncForm(form) {
    const select = form.querySelector('select[name="id"]');
    if (!select) return;

    const selectedValues = this._readSelection(form);
    const variantOption = this._findVariantOption(select, selectedValues);

    if (variantOption) {
      select.value = variantOption.value;
      Array.from(select.options).forEach((option) => {
        option.selected = option === variantOption;
      });
    } else {
      Array.from(select.options).forEach((option) => {
        option.selected = false;
      });
      select.selectedIndex = -1;
      select.value = '';
    }

    this._setButtonState(form, variantOption);

    if (variantOption) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantOption.value);
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }

    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  destroy() {
    this._section.removeEventListener('change', this._onChange);
  }
}

class PdpPriceVisibilityController {
  constructor(section) {
    this._section = section;
    this._priceBlocks = Array.from(section.querySelectorAll('[data-pdp-price-block]'));
    this._onChange = this._onChange.bind(this);
    section.addEventListener('change', this._onChange);
    this._syncInitial();
  }

  _isColorInput(input) {
    if (!(input instanceof HTMLInputElement)) return false;
    const optionName = input.dataset.pdpOptionName || '';
    return /^(cor|color)(-|$)/.test(optionName);
  }

  _isInputAvailable(input) {
    if (!input) return this._section.dataset.pdpProductAvailable === 'true';
    const label = input.closest('.pdp-bar__option-label');
    return !label?.classList.contains('is-sold-out');
  }

  _apply(available) {
    this._priceBlocks.forEach((block) => {
      block.hidden = !available;
    });
  }

  _syncInitial() {
    const selectedColorAvailable = this._section.dataset.pdpSelectedColorAvailable;
    if (selectedColorAvailable === 'true' || selectedColorAvailable === 'false') {
      this._apply(selectedColorAvailable === 'true');
      return;
    }

    const selectedColor = this._section.querySelector(
      '[data-pdp-option-name^="cor"]:checked, [data-pdp-option-name^="color"]:checked'
    );
    this._apply(this._isInputAvailable(selectedColor));
  }

  _onChange(event) {
    const target = event.target;

    if (target instanceof HTMLSelectElement && target.matches('select[name="id"]')) {
      const selectedOption = target.selectedOptions?.[0];
      if (!selectedOption?.value) {
        const selectedColor = this._section.querySelector(
          '[data-pdp-option-name^="cor"]:checked, [data-pdp-option-name^="color"]:checked'
        );
        this._apply(this._isInputAvailable(selectedColor));
        return;
      }

      const available = selectedOption.dataset.available === 'true';
      const price = Number(selectedOption.dataset.price || 0);
      this._apply(available && price > 0);
      return;
    }

    if (!this._isColorInput(target)) return;
    this._apply(this._isInputAvailable(target));
  }

  destroy() {
    this._section.removeEventListener('change', this._onChange);
  }
}

class PdpManager {
  constructor({ sectionId }) {
    this._section = document.querySelector(`.pdpMain--${sectionId}`);
    if (!this._section) return;

    this._modules = [];
    this._modules.push(new VariantImageFilterController(this._section));
    this._modules.push(new PdpPriceVisibilityController(this._section));
    this._modules.push(new VariantSelectionController(this._section));

    this._section.querySelectorAll('[data-product-wrapper]').forEach(wrapper => {
      this._modules.push(new AddToCartController(wrapper));
    });
  }

  destroy() {
    this._modules.forEach(m => m.destroy?.());
    this._modules = [];
  }
}

/* ─── Inicialização ───────────────────────────────────────────────────────── */

function initPdpManager(sectionId) {
  if (window._pdpManager) window._pdpManager.destroy();
  window._pdpManager = new PdpManager({ sectionId });
}

window.initPdpManager = initPdpManager;

// Auto-inicialização: escaneia o DOM por seções PDP declaradas via data attribute.
// Resolve o problema de timing onde {% javascript %} executa antes deste script.
document.querySelectorAll('[data-pdp-section-id]').forEach(el => {
  initPdpManager(el.dataset.pdpSectionId);
});
